import React, { useEffect, useReducer, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { MdAdd, MdArrowBack } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import PropTypes from 'prop-types';
import CustomTypeahead from '../../components/CustomTypeahead';
import FormatedNumberInput from '../../components/FormatedNumberInput';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import { Button, FormField, Section, Select, controlClass } from '../../components/EntityList';
import assetService from '../../services/asset.service';
import creditService from '../../services/credit.service';
import debtService from '../../services/debt.service';
import warrantyService from '../../services/warranty.service';
import { currencies, getError } from '../../utils';

const OPTIONS = [
	{ value: 'deuda', label: 'Deuda' },
	{ value: 'activo', label: 'Activo' },
	{ value: 'efectivo', label: 'Efectivo' },
];

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FIELD_LABELS = {
	option: 'Opción de garantía',
	debt: 'Deuda',
	asset: 'Activo',
	currency: 'Moneda',
	amount: 'Monto',
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_CREDIT_SUCCESS':
		return { ...state, credit: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	default:
		return state;
	}
};

const WarrantyEditor = ({ preloadCreditId = null, onCancel, onSaved, cancelLabel = 'Cancelar', saveLabel }) => {
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	// Ids injected via ?debtId=/?propertyId= after a "crear nueva deuda" /
	// "crear nuevo activo" round trip.
	const injectedDebtId = searchParams.get('debtId');
	const injectedAssetId = searchParams.get('propertyId');
	const consumedDebtId = useRef(null);
	const consumedAssetId = useRef(null);

	const [{ loading, saving, credit, error }, dispatch] = useReducer(reducer, {
		loading: !!preloadCreditId,
		saving: false,
		credit: null,
		error: '',
	});

	const [option, setOption] = useState('');
	const [debt, setDebt] = useState(null);
	const [asset, setAsset] = useState(null);
	const [amount, setAmount] = useState('');
	const [currency, setCurrency] = useState('');
	const [maximumAmount, setMaximumAmount] = useState(0);
	const [filteredAssetsList, setFilteredAssetsList] = useState([]);
	const [filteredDebtsList, setFilteredDebtsList] = useState([]);
	const [loadingOptions, setLoadingOptions] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});

	// Keep the URL mirroring the current selection so a manual pick or clear
	// doesn't leave a stale id from an earlier round trip to be re-injected.
	const syncInjectedParam = (key, id, consumedRef) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		if (nextValue) consumedRef.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	// Auto-select a debt created via the "crear nueva deuda" link. Unlike
	// filteredDebtsList (only populated once an option+credit are known),
	// this fetches the record directly so the round trip works regardless of
	// what's selected yet.
	useEffect(() => {
		if (!injectedDebtId || consumedDebtId.current === injectedDebtId) return;
		consumedDebtId.current = injectedDebtId;
		(async () => {
			try {
				const { data } = await debtService.getDetails(injectedDebtId);
				setOption('deuda');
				setDebt(data);
				setAmount(data.capital || 0);
				clearFieldError('option');
				clearFieldError('debt');
			} catch (fetchError) {
				console.error(fetchError);
				toast.error('No se pudo obtener deuda');
			}
		})();
	}, [injectedDebtId]);

	// Auto-select an asset created via the "crear nuevo activo" link, same
	// round trip as the debt handling above.
	useEffect(() => {
		if (!injectedAssetId || consumedAssetId.current === injectedAssetId) return;
		consumedAssetId.current = injectedAssetId;
		(async () => {
			try {
				const { data } = await assetService.getDetails(injectedAssetId);
				setOption('activo');
				setAsset(data);
				const maxAmount = data.price - (data.guaranteedAmount || 0);
				setMaximumAmount(maxAmount);
				setAmount(maxAmount);
				clearFieldError('option');
				clearFieldError('asset');
			} catch (fetchError) {
				console.error(fetchError);
				toast.error('No se pudo obtener activo');
			}
		})();
	}, [injectedAssetId]);

	useEffect(() => {
		const fetchCreditData = async () => {
			if (!preloadCreditId) return;
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const creditData = await creditService.getDetails(preloadCreditId);
				dispatch({ type: 'FETCH_CREDIT_SUCCESS', payload: creditData.data });
				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (fetchError) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(fetchError) });
				toast.error('No se pudo obtener pasividad');
			}
		};
		fetchCreditData();
	}, [preloadCreditId]);

	useEffect(() => {
		const fetchOptionData = async () => {
			if (!option || !credit) return;

			setLoadingOptions(true);
			try {
				if (option === 'activo') {
					const assetsData = await assetService.getForWarranty();
					const filteredAssets = assetsData.data.filter(
						(a) => a.currency === credit.currency,
					);
					setFilteredAssetsList(filteredAssets);
				} else if (option === 'deuda') {
					const debtsData = await debtService.getForWarranty();
					const filteredDebts = debtsData.data.filter(
						(d) => d.currency === credit.currency,
					);
					setFilteredDebtsList(filteredDebts);
				}
			} catch (fetchError) {
				toast.error('Error al cargar datos');
			}
			setLoadingOptions(false);
		};
		fetchOptionData();
	}, [option, credit]);

	const handleOptionChange = (event) => {
		setOption(event.target.value);
		setDebt(null);
		setAsset(null);
		setAmount(0);
		setMaximumAmount(0);
		setCurrency('');
		clearFieldError('option');
	};

	const handleDebtChange = (selected) => {
		if (!selected || selected.length === 0) {
			setDebt(null);
			setAmount(0);
			syncInjectedParam('debtId', null, consumedDebtId);
		} else {
			const selectedDebt = selected[0];
			setDebt(selectedDebt);
			setAmount(selectedDebt.capital || 0);
			clearFieldError('debt');
			syncInjectedParam('debtId', selectedDebt._id, consumedDebtId);
		}
	};

	const handleAssetChange = (selected) => {
		if (!selected || selected.length === 0) {
			setAsset(null);
			setAmount(0);
			setMaximumAmount(0);
			syncInjectedParam('propertyId', null, consumedAssetId);
		} else {
			const selectedAsset = selected[0];
			setAsset(selectedAsset);
			const maxAmount = selectedAsset.price - (selectedAsset.guaranteedAmount || 0);
			setMaximumAmount(maxAmount);
			setAmount(maxAmount);
			clearFieldError('asset');
			syncInjectedParam('propertyId', selectedAsset._id, consumedAssetId);
		}
	};

	const handleAmountChange = (e) => {
		// Keep the raw string in state so a decimal separator survives while it is being typed.
		const raw = e.target.value;
		const parsed = parseFloat(raw);
		const withinMax = raw === '' || (!isNaN(parsed) && parsed <= maximumAmount);
		if (option === 'activo' && maximumAmount && withinMax) {
			setAmount(raw);
		} else if (option === 'deuda' || option === 'efectivo') {
			setAmount(raw);
		} else if (option === 'activo' && !maximumAmount) {
			setAmount(raw);
		}
		clearFieldError('amount');
	};

	const getFieldErrors = () => {
		const errors = {};
		if (!option) errors.option = REQUIRED_FIELD_MESSAGE;
		if (option === 'deuda' && !debt) errors.debt = REQUIRED_FIELD_MESSAGE;
		if (option === 'activo' && !asset) errors.asset = REQUIRED_FIELD_MESSAGE;
		if (option === 'efectivo' && !currency) errors.currency = REQUIRED_FIELD_MESSAGE;
		if (!amount || amount <= 0) errors.amount = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const handleSave = async (event) => {
		event.preventDefault();
		if (loading || saving) return;

		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			dispatch({
				type: 'SAVE_FAIL',
				payload: `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`,
			});
			return;
		}
		if (!preloadCreditId) {
			dispatch({ type: 'SAVE_FAIL', payload: 'Debe especificar una pasividad.' });
			return;
		}
		setFieldErrors({});

		try {
			dispatch({ type: 'SAVE_REQUEST' });
			await warrantyService.create({
				option,
				debt: debt?._id || debt,
				asset: asset?._id || asset,
				amount: Number(amount),
				currency: option === 'efectivo' ? currency : undefined,
				credit: preloadCreditId,
			});
			dispatch({ type: 'SAVE_SUCCESS' });
			toast.success('Garantía creada');
			if (onSaved) {
				onSaved();
			}
		} catch (ex) {
			const message = getError(ex);
			dispatch({ type: 'SAVE_FAIL', payload: message });
			toast.error(message);
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	const resolvedSaveLabel = saveLabel || 'Agregar';

	return (
		<Form id='warranty-editor-form' onSubmit={handleSave} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					Completá los datos para crear la garantía.
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					<Button variant='primary' icon={<MdAdd />} type='submit' disabled={saving || loading}>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section title='Garantía'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='warranty-option' label={FIELD_LABELS.option} required>
						<Select
							id='warranty-option'
							options={OPTIONS}
							value={option}
							onChange={handleOptionChange}
							placeholder='Elegí una opción'
							disabled={saving}
						/>
						<FieldError message={fieldErrors.option} />
					</FormField>
					{option === 'deuda' && (
						<FormField id='warranty-debt' label={FIELD_LABELS.debt} required>
							<CustomTypeahead
								id='warranty-debt'
								labelKey={(opt) => opt?.customId || ''}
								onChange={handleDebtChange}
								options={filteredDebtsList}
								placeholder='Buscar deuda...'
								selected={debt ? [debt] : []}
								disabled={saving || loadingOptions}
								linkToCreate={`/AdminScreen/newDebt?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nueva deuda'
								variant='entity'
							/>
							<FieldError message={fieldErrors.debt} />
						</FormField>
					)}
					{option === 'activo' && (
						<FormField id='warranty-asset' label={FIELD_LABELS.asset} required>
							<CustomTypeahead
								id='warranty-asset'
								labelKey={(opt) => opt?.customId || ''}
								onChange={handleAssetChange}
								options={filteredAssetsList}
								placeholder='Buscar activo...'
								selected={asset ? [asset] : []}
								disabled={saving || loadingOptions}
								linkToCreate={`/AdminScreen/newAsset?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nuevo activo'
								variant='entity'
							/>
							<FieldError message={fieldErrors.asset} />
						</FormField>
					)}
					{option === 'efectivo' && (
						<FormField id='warranty-currency' label={FIELD_LABELS.currency} required>
							<Select
								id='warranty-currency'
								options={currencies}
								value={currency}
								onChange={(event) => {
									setCurrency(event.target.value);
									clearFieldError('currency');
								}}
								placeholder='Elegí Moneda'
								disabled={saving}
							/>
							<FieldError message={fieldErrors.currency} />
						</FormField>
					)}
					<FormField id='warranty-amount' label={FIELD_LABELS.amount} required>
						<FormatedNumberInput
							id='warranty-amount'
							type='number'
							className={controlClass}
							value={amount}
							onChange={handleAmountChange}
							disabled={saving || (option === 'deuda' && !!debt)}
							max={option === 'activo' && maximumAmount ? maximumAmount : undefined}
							min={0}
						/>
						{option === 'activo' && maximumAmount > 0 && (
							<p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-ink-3'>
								Monto máximo disponible: {maximumAmount}
							</p>
						)}
						<FieldError message={fieldErrors.amount} />
					</FormField>
				</div>
			</Section>
		</Form>
	);
};

WarrantyEditor.propTypes = {
	preloadCreditId: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
};

export default WarrantyEditor;
